import { EventBus, GameEvents } from '@/events/EventBus';
import { notifyProfileChanged } from '@/config/ProfileSync';

// Global settings key (not per-slot — settings are shared)
const SETTINGS_KEY = 'robogame_settings';

// ── Game settings (persisted across sessions) ──
export interface GameSettings {
  difficultyMult: number;
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
  fullscreen: boolean;
  crtEnabled: boolean;
  crtScanlines: number;   // 0–1: scanline intensity
  crtWarp: number;         // 0–1: barrel distortion
  crtVignette: number;     // 0–1: corner darkening
  devMode: boolean;        // Show dev buttons (toggled via console: god ON/OFF)
  language: string;        // Locale code: 'en', 'fr', etc.
  contextHelp: boolean;    // Show cursor hints on tile hover
}

// ── Score entry for a single level attempt ──
export interface ScoreEntry {
  levelId: string;
  date: string;
  won: boolean;
  wavesCompleted: number;
  enemiesKilled: number;
  stars: number;
  totalScore?: number;
  maxScore?: number;
  isPlatinum?: boolean;
}

// ── Global settings data (not per-slot) ──
interface SettingsData {
  settings: GameSettings;
  scoreHistory: ScoreEntry[];
}

const DEFAULT_SETTINGS: GameSettings = {
  difficultyMult: 1.0,
  musicVolume: 0.2,
  sfxVolume: 0.5,
  muted: false,
  fullscreen: false,
  crtEnabled: false,
  crtScanlines: 0.7,
  crtWarp: 0.6,
  crtVignette: 0.5,
  devMode: false,
  language: 'en',
  contextHelp: false,
};

/**
 * Centralized save system for global settings and score history.
 * Designs are per-slot (managed by SlotManager via ProgressionManager.save()).
 * Settings (volume, difficulty) are global — shared across all slots.
 */
class SaveManagerClass {
  private settings: GameSettings = { ...DEFAULT_SETTINGS };
  private scoreHistory: ScoreEntry[] = [];

  constructor() {
    this.load();
  }

  // ── Settings (global, not per-slot) ──

  getSettings(): GameSettings { return { ...this.settings }; }

  updateSettings(partial: Partial<GameSettings>): void {
    Object.assign(this.settings, partial);
    this.save();
    EventBus.emit(GameEvents.SETTINGS_CHANGED, this.settings);
    notifyProfileChanged(); // sync game settings to default-profile.json
  }

  // ── Score history ──

  getScoreHistory(): ScoreEntry[] { return this.scoreHistory; }

  addScore(entry: ScoreEntry): void {
    this.scoreHistory.push(entry);
    if (this.scoreHistory.length > 200) {
      this.scoreHistory = this.scoreHistory.slice(-200);
    }
    this.save();
    EventBus.emit(GameEvents.SCORE_SAVED, entry);
  }

  getScoresForLevel(levelId: string): ScoreEntry[] {
    return this.scoreHistory.filter(s => s.levelId === levelId);
  }

  getBestScore(levelId: string): ScoreEntry | undefined {
    return this.getScoresForLevel(levelId)
      .filter(s => s.won)
      .sort((a, b) => b.stars - a.stars || b.enemiesKilled - a.enemiesKilled)[0];
  }

  // ── Persistence (global settings only) ──

  private save(): void {
    const data: SettingsData = {
      settings: this.settings,
      scoreHistory: this.scoreHistory,
    };
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(data));
    } catch (e) { console.warn('[SaveManager] save failed:', e); }
  }

  private load(): void {
    try {
      // Try new key first
      let raw = localStorage.getItem(SETTINGS_KEY);
      if (!raw) {
        // Fallback: migrate from old single-save key
        raw = localStorage.getItem('robogame_save');
      }
      if (!raw) return;
      const data = JSON.parse(raw);
      this.settings = { ...DEFAULT_SETTINGS, ...data.settings };
      this.scoreHistory = data.scoreHistory ?? [];
    } catch (e) {
      console.warn('[SaveManager] load failed:', e);
      this.settings = { ...DEFAULT_SETTINGS };
      this.scoreHistory = [];
    }
  }

  /** Clear score history (called when active save slot is deleted) */
  clearScoreHistory(): void {
    this.scoreHistory = [];
    this.save();
  }

  /** Reset all save data (for debug/testing) */
  reset(): void {
    this.settings = { ...DEFAULT_SETTINGS };
    this.scoreHistory = [];
    this.save();
  }
}

export const SaveManager = new SaveManagerClass();
